<template lang="pug">
  span
    span(@click="modal_open")
      slot(name="activator")
    liondesk-dialog(
      v-model="isModalVisible",
      hide-ok-button,
      dialog-title="Bulk Action - Updated Status",
      cancel-button-text='Close',
      width="800px"
      @click:cancel='modal_close',
    )
      div(slot='body')
        v-tabs(slider-color="primary" v-model="currentTab")
          v-tab(@click="changeTab('success')")
            span successful
          v-tab(@click="changeTab('failed')")
            span errors
          
          v-tab-item
            v-layout.mt-3(row)
              v-flex
                v-card
                  v-data-table.reporting-table(
                    hide-actions
                    :headers="successfulHeaders"
                    :items="contacts"
                    :loading="isLoadingSelectedBulkAction"
                  )
                    template(slot="items" slot-scope="data")
                      tr
                        td(v-if="isDeleteContactsBulkAction") 
                          | {{ getContactName(data.item.first_name, data.item.last_name) }}
                        td(v-else) 
                          a(:href="'/contacts/'+ data.item.id" target="_blank")
                            | {{ getContactName(data.item.first_name, data.item.last_name) }}
                        td {{ data.item.email }}
                        td {{ data.item.date_created | formatDate }}
            v-layout.pagination
              v-flex.pages
                v-pagination(
                  v-if="totalPages > 1"
                  :value="page"
                  :length="totalPages"
                  @input="setPage"
                  :total-visible="7"
                )
              v-flex.limits
                pagination-limit.mr-4(
                  :limits="paginationLimits"
                  :current-limit="limit"
                  @change:limit="changeLimit"
                )
          v-tab-item
            v-layout.mt-3(row)
              v-flex
                v-card
                  v-data-table.reporting-table(
                    hide-actions
                    :headers="failedHeaders"
                    :items="contacts"
                    :loading="isLoadingSelectedBulkAction"
                  )
                    template(slot="items" slot-scope="data")
                      tr
                        td(v-if="isDeleteContactsBulkAction") 
                          | {{ getContactName(data.item.first_name, data.item.last_name) }}
                        td(v-else) 
                          a(:href="'/contacts/'+ data.item.id" target="_blank")
                            | {{ getContactName(data.item.first_name, data.item.last_name) }}
                        td {{ data.item.email }}
                        td {{ data.item.date_created | formatDate }}
                        td {{ data.item.error }}
            v-layout.pagination
              v-flex.pages
                v-pagination(
                  v-if="totalPages > 1"
                  :value="page"
                  :length="totalPages"
                  @input="setPage"
                  :total-visible="7"
                )
              v-flex.limits
                pagination-limit.mr-4(
                  :limits="paginationLimits"
                  :current-limit="limit"
                  @change:limit="changeLimit"
                )
      template(slot='actions')
        v-tooltip.mr-2(top :disabled="!shouldDisableStartCampaignButton")
          v-btn.primary(
            v-if="shouldShowStartCampaign"
            slot="activator"
            :disabled="shouldDisableStartCampaignButton"
            @click="openStartCampaignModal"
          ) Start A New Campaign
          span The action is pending to complete
        v-tooltip(
          v-if="shouldShowStartCampaign"
          top
          max-width="200"
        )
          v-icon(slot='activator' medium) help_outline
          span Start a new campaign for all successful contacts
        span(v-if="status == 'failed'") If the error persists, please&nbsp;
          a(:href="contactSupportLink") contact support
        v-spacer
        v-btn(flat @click="modal_close") Close
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import LiondeskDialog from '@/Core/components/LiondeskDialog';
import PaginationLimit from '@/Core/components/PaginationLimit';
import Modal from '@/Core/mixins/modal';
import Pagination from '@/Core/mixins/pagination';
import { formatDate, formatShortDate } from '@/Core/filters/dates';
import { successfulHeaders, failedHeaders } from '../config/bulk-actions-report-options';

export default {
  name: 'BulkActionsContactsModal',
  props: {
    bulkActionId: {
      type: String,
    },
    bulkActionStatus: {
      type: String,
    },
    dateCreated: {
      type: String,
    },
  },
  components: {
    LiondeskDialog,
    PaginationLimit,
    StartCampaignModal: () => import(/* webpackChunkName: 'CustomNumberDialog' */ '@/Contacts/components/actions/StartCampaignModal'),
  },
  mixins: [Modal, Pagination],
  filters: { formatDate },
  data() {
    return {
      currentTable: 'bulk-action-contacts',
      currentTab: 0,
      status: 'success',
      page: 1,
      limit: 10,
      successfulHeaders: successfulHeaders,
      failedHeaders: failedHeaders,
    };
  },
  watch: {
    isModalVisible: {
      handler(value) {
        if (value) {
          this.getContacts();
          return
        }
        this.page = 1;
        this.status = 'success';
        this.currentTab = 0;
      },
    },
  },
  computed: {
    ...mapState('bulkActionsReport', ['contacts', 'contactsTotal', 'isLoadingSelectedBulkAction']),
    ...mapGetters('bulkActionsReport', ['isDeleteContactsBulkAction', 'isStopCampaignBulkAction']),
    totalPages() {
      return Math.ceil(this.contactsTotal / this.limit);
    },
    shouldDisableStartCampaignButton() {
      if (!this.bulkActionStatus) return;
      return this.bulkActionStatus === 'pending';
    },
    shouldShowStartCampaign() {
      return this.isStopCampaignBulkAction && !this.isLoadingSelectedBulkAction && this.status === 'success';
    },
    contactSupportLink() {
      const dateFormated = formatShortDate(this.dateCreated);
      const subject = encodeURIComponent(`Bulk Action Error: Status - Job ID ${this.bulkActionId}`);
      const body = encodeURIComponent(`My bulk action job - ID ${this.bulkActionId}, run on ${dateFormated} - had persistent errors that I have seen in other bulk action reports.`);
      return `mailto:support@liondesk.com?subject=${subject}&body=${body}`;
    },
  },
  methods: {
    ...mapActions('bulkActionsReport', ['getBulkActionsContacts']),
    getContactName(first, last) {
      const fullName = `${first} ${last}`;
      const unnamed = '(Unnamed Contact)';

      return fullName.trim() || unnamed;
    },
    changeLimit(newLimit) {
      this.limit = newLimit;
      this.page = 1;
      this.getContacts();
    },
    setPage(newPage) {
      this.page = newPage;
      this.getContacts();
    },
    hasMoreThanOnePage() {
      return this.totalPages > 1;
    },
    changeTab(val) {
      this.status = val;
      this.page = 1;
      this.getContacts();
    },
    getContacts() {
      const params = {
        bulkActionId: this.bulkActionId,
        status: this.status,
        limit: this.limit,
        page: this.page,
      };

      this.getBulkActionsContacts(params)
    },
    openStartCampaignModal() {
      this.$emit('start-campaign');
      this.modal_close();
    },
  },
}
</script>

<style lang="scss" scoped>
.reporting-table /deep/ .v-table__overflow {
  overflow-y: scroll;
  max-height: 35vh;
  overflow-x: scroll;
  min-width: inherit;

  tbody td, thead tr {
    height: 55px;
  }

  tbody {
    tr:hover {
      box-shadow: none;
    }

    td {
      vertical-align: middle !important;
    }
  }

  thead {
    th {
      position: -webkit-sticky;
      position: sticky;
      background-color: #fff;
      top: 0;
      z-index: 1;
    }
  }
}

.pagination {
  height: 60px;
}
</style>
